import { reconcileSessionRepository } from '@webapp/modules/accounts-payable/data-access/repositories/ReconcileSessionRepository'
import { type StatementAttachmentType } from '@webapp/modules/accounts-payable/entities/enums/StatementAttachmentTypeEnum'
import { type ReconcileSessionConstructor } from '@webapp/modules/accounts-payable/entities/ReconcileSession'

// Use case owns its own narrow input types — no UI/form/DS types.
export interface StartReconciliationSessionParams {
  creditcardId: number
  /** Existing DRAFT session id — reused instead of creating one (BHV-028) */
  existingSessionId: Nullable<number>
  /** YYYY-MM-DD — required when creating a session (full mode) */
  statementDate: Nullable<string>
  balance?: string
  /** Uploaded statement attachment — statement record created only when set */
  attachmentId: Nullable<string>
  attachmentType: Nullable<StatementAttachmentType>
}

export interface StartReconciliationSessionResult {
  /** Newly created session; null when an existing session was reused */
  session: Nullable<ReconcileSessionConstructor>
}

// Orchestrates session create-or-reuse + the conditional statement record
// (BHV-028): a session is created only when the account has none; the
// statement record is created only when an attachment was uploaded.
export const startReconciliationSession = async (
  params: StartReconciliationSessionParams
): Promise<StartReconciliationSessionResult> => {
  let session: Nullable<ReconcileSessionConstructor> = null

  if (params.existingSessionId === null) {
    session = await reconcileSessionRepository.createSession({
      creditcardId: params.creditcardId,
      balance: params.balance,
      statementDate: params.statementDate ?? '',
    })
  }

  if (params.attachmentId !== null && params.attachmentType !== null) {
    await reconcileSessionRepository.createStatement({
      creditcardId: params.creditcardId,
      attachmentId: params.attachmentId,
      attachmentType: params.attachmentType,
    })
  }

  return { session }
}
